/**
 * useAmlGate — route guard for Qualitas AML questionnaire.
 *
 * Complements useVerificationGate: after KYC, users still need the
 * AML form (which includes the Qualitas declaration) before booking or hosting.
 *
 * Does NOT fire while KYC is incomplete (verification gate owns that path).
 *
 * mode:
 *  - 'guest'  → needs form submitted (under_review or approved)
 *  - 'host'   → needs host-ready status (see EARLY_ACCESS.amlHostRequiresPartnerApproval)
 */
import { useEffect, useRef } from 'react'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { useAmlReadiness } from './use-aml-compliance'
import { verificationService } from '../services/verification.service'
import { toast } from '../toast'
import { markBookingComplianceDetour } from '@/lib/navigation/booking-detour'

export type AmlGateMode = 'guest' | 'host'

export interface UseAmlGateOptions {
  /**
   * Whether the in-flight booking carries platform (Qualitas) coverage.
   *   - `false` → the trip carries no premium; the AML declaration is never
   *     demanded, whatever `aml_declarations` says.
   *   - `true`  → platform coverage; the gate behaves exactly as before.
   *   - `null`/`undefined` → coverage not resolved yet (vehicle still loading);
   *     the gate keeps `isLoading` true and does NOT redirect. Legacy callers
   *     that omit it keep the old behavior (declaration required).
   */
  requiresPlatformCoverage?: boolean | null
}

export interface UseAmlGateResult {
  /** True when auth+aml loaded AND user fails the gate. Render null while true. */
  gated: boolean
  /** Still resolving declaration / KYC. */
  isLoading: boolean
}

export function useAmlGate(
  mode: AmlGateMode = 'guest',
  options: UseAmlGateOptions = {},
): UseAmlGateResult {
  const router = useRouter()
  const { t } = useTranslation()
  const { profile, isLoading: authLoading } = useAuth()
  const profileId = profile?.id ?? ''
  const aml = useAmlReadiness()
  const dispatchedRef = useRef(false)
  const requiresPlatformCoverage = options.requiresPlatformCoverage ?? null

  const kycQuery = useQuery({
    queryKey: ['verification-gate', profileId, 'for-aml'],
    queryFn: () => verificationService.getOverview(profileId),
    enabled: !authLoading && !!profileId && profile?.isVerified !== true,
    staleTime: 15_000,
  })

  const kycOk = profile?.isVerified === true || kycQuery.data?.isFullyVerified === true
  const kycPending =
    !authLoading &&
    !!profile &&
    profile.isVerified !== true &&
    (kycQuery.isPending || (!kycOk && kycQuery.isFetching))

  // P3 — EL DESACOPLE DE LA PRIMA. La declaración Qualitas solo se exige cuando
  // la reserva lleva cobertura de plataforma. Mientras el vehículo no resolvió
  // su cobertura, la compuerta NO sabe si la declaración aplica: permanece en
  // `isLoading` y no redirige a nadie antes de tiempo.
  const coverageKnown = requiresPlatformCoverage === true || requiresPlatformCoverage === false
  const ready =
    mode === 'host' ? aml.isHostReady : requiresPlatformCoverage === false ? true : aml.isComplete
  const loading =
    authLoading || kycPending || (!!profile && aml.isLoading) || (!!profile && !coverageKnown)

  useEffect(() => {
    if (loading) return
    if (!profile) return
    // Let useVerificationGate own the KYC redirect.
    if (!kycOk) return
    if (ready) return
    if (dispatchedRef.current) return
    dispatchedRef.current = true

    toast.info(t('aml.gate_required_toast'))
    // Mark this as a compliance detour so BookingLayout does not treat the
    // resulting unmount as an abandonment and wipe the guest's booking draft.
    markBookingComplianceDetour()
    router.replace('/(tabs)/profile/verification/compliance' as never)
  }, [loading, profile, kycOk, ready, router, t])

  const gated = !loading && !!profile && kycOk && !ready
  return { gated, isLoading: loading }
}
